import { TableColumnsType } from "antd";
import {
  CommonTable,
  DashboardCardDetailTable,
} from "components/atoms/table/AntdTable";
import {
  SubscriptionDetailSummaryResult,
  SUBSCRIPTION_TYPE,
  SelectedRowAccountInfo,
  SubscriptionDetailSummaryBase,
} from "api/interfaces/subscription.interface";
import StorageBar from "components/blocks/storage/StorageBar";
import styled from "styled-components";
import { isMobile } from "components/MediaQuery";
import { formatNumber } from "utils/functions";

import TinyButton from "components/atoms/buttons/TinyButton";
import { useCallback, useMemo, useState } from "react";
import InputDate from "components/atoms/calendar/InputDate";
import SubscriptionStorageChangeModal from "pages/subscription/modal/activate/SubscriptionStorageChangeModal";
import { FLEX } from "styles/flex";
import { FaArrowsRotate } from "react-icons/fa6";
import RBACWrapper from "components/blocks/function/RBACWrapper";

import * as mediaQuery from "components/MediaQuery";
import SubscriptonCotermWork from "../../modal/howCotermWork/SubscriptonCotermWorkModal";
import {
  differenceInDays,
  getAccountTimeFormatDate,
  getUtcDate,
} from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import moment from "moment";
import { selectSubscriptionStomp } from "redux/stomp/stompSelector";
import useApiError from "hook/useApiError";
import {
  getSubscriptionDetailSummary,
  postCovaFreeKeyRenew,
  postCovaFreeKeyReset,
} from "api/subscriptionAPI";
import { useQuery, useQueryClient } from "react-query";
import { COLORS } from "styles/colors";
import { calcRem } from "styles/theme";
import { useFlag } from "@unleash/proxy-client-react";
import useStatusMutation from "hook/useStatusMutation";
import { CustomError } from "api/interfaces/commonInterface.interface";

type Props = {
  type: SUBSCRIPTION_TYPE;
  selectedRowAccount: SelectedRowAccountInfo;
};

type SummaryRow = SubscriptionDetailSummaryBase & {
  key: string;
};

const formatDurationYears = (durationMonths: number): string => {
  const years = Math.floor(durationMonths / 12);
  const months = durationMonths % 12;

  const yearsLabel = years > 0 ? `${years}${years === 1 ? "yr" : "yrs"}` : "";
  const monthsLabel =
    months > 0 ? `${months}${months === 1 ? "mo" : "mos"}` : "";

  return [yearsLabel, monthsLabel].filter(Boolean).join(" ");
};

const buildCovaFreeLabel = (
  extendedKeys?: SubscriptionDetailSummaryBase["extendedKeys"]
): string => {
  if (!extendedKeys || extendedKeys.length === 0) {
    return "Basic(1yr)";
  }

  const totalExtraDuration = extendedKeys.reduce(
    (sum, ek) => sum + ek.duration,
    0
  );

  return `Basic(1yr) + Extra(${formatDurationYears(totalExtraDuration)})`;
};

export default function HistoryDetailSummary(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { handleErrorNotify, lang } = useApiError();
  const { subscriptionActivatedUpdated } = useAppSelector(
    selectSubscriptionStomp
  );

  const [isModalStorageChange, setIsModalStorageChange] =
    useState<boolean>(false);
  const [isModalCoterm, setIsModalCoterm] = useState<boolean>(false);

  const isDesktop = mediaQuery.useIsDesktop();
  const isStorage = props.type === SUBSCRIPTION_TYPE.STORAGE;
  const isStandard = props.type === SUBSCRIPTION_TYPE.STANDARD;
  const isCovaFreeKeyRenew = useFlag("feature_subscription_cova_renew");

  const buildTableRows = useCallback(
    (data: SubscriptionDetailSummaryResult): SummaryRow[] => {
      const { covaList = [], ...base } = data;

      const rows: SummaryRow[] = [];

      if (props.type === SUBSCRIPTION_TYPE.STANDARD) {
        if (base.registeredChannel && base.registeredChannel > 0) {
          rows.push({
            ...base,
            key: "base",
          });
        }

        const covaRows: SummaryRow[] = (covaList ?? []).map((cova, idx) => ({
          ...cova,
          key: `cova-${idx}`,
          registered: 0,
        }));
        return rows.concat(covaRows);
      }
      return [{ ...base, key: "base" }];
    },
    [props.type]
  );

  const onModal = () => {
    setIsModalStorageChange(!isModalStorageChange);
  };

  const onModalCotermWork = () => {
    setIsModalCoterm(!isModalCoterm);
  };

  const { data: summaryData } = useQuery(
    [
      "getSubscriptionDetailSummary",
      props.selectedRowAccount.accountId,
      props.type,
      subscriptionActivatedUpdated,
    ],
    () =>
      getSubscriptionDetailSummary({
        accountId: props.selectedRowAccount.accountId,
        type: props.type as SUBSCRIPTION_TYPE,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onError: (_e: any) => {
        handleErrorNotify(_e, lang.label.subscription.detail.summary.fail);
      },
    }
  );
  const columns = (isCova?: boolean): TableColumnsType<SummaryRow> => {
    return [
      {
        title: "COVA Title",
        dataIndex: "recorderName",
        render: (value, row) => value ?? "-",
        hidden: !isCova || isStorage,
      },
      {
        title: "Registered Channels",
        dataIndex: "registeredChannel",
        render: (value, row) => formatNumber(value),
        width: !isDesktop ? "25%" : 200,
        hidden: isStorage || isCova,
      },
      {
        title: isCova ? "Channels" : `Activated Storage (TB)`,
        dataIndex: "activated",
        render: (value, row) => {
          const defaultValue = value ? formatNumber(value) : "-";
          const isExpired = Boolean(row.expiration)
            ? differenceInDays(row.expiration, getUtcDate(new Date())) < 0
            : false;

          return (
            <ActivatedValue>
              {defaultValue}
              {isStorage && value > 0 && (
                <RBACWrapper requiredPermissions="manageSubscription">
                  <TinyButton
                    label="Change"
                    onClickBtn={() => {
                      if (isExpired) {
                        return;
                      }
                      onModal();
                    }}
                    icon={<FaArrowsRotate />}
                    disabled={row.expiration ? isExpired : true}
                  />
                </RBACWrapper>
              )}
            </ActivatedValue>
          );
        },
        width: !isDesktop ? "25%" : "15%",
        hidden: !isCova,
      },
      {
        title: (
          <div className="expire-title">
            Expiration{" "}
            {isStandard && !isCova && (
              <TinyButton label="Calculator" onClickBtn={onModalCotermWork} />
            )}
          </div>
        ),
        dataIndex: "expiration",
        render: (value, row) => {
          return (
            <ExpireRow>
              {value === "" || !Boolean(value)
                ? "-"
                : getAccountTimeFormatDate(value, {
                    ...selectedAccount,
                  })}
              {isCova && (
                <span className="cova-free">
                  {buildCovaFreeLabel(row.extendedKeys)}
                </span>
              )}
              {isCova && isCovaFreeKeyRenew && (
                <CovaFreeKeyRenew
                  accountId={props.selectedRowAccount.accountId}
                  dwKey={row.dwKey}
                />
              )}
            </ExpireRow>
          );
        },
        width: isStorage ? "15%" : undefined,
      },
      {
        title: "Usage",
        dataIndex: "registered",
        render: (value, row) => {
          return (
            <BarWrapper>
              <StorageBar
                usedStorage={value}
                totalStorage={row.activatedCamera || 0}
              />
            </BarWrapper>
          );
        },
        hidden: isStandard,
        responsive: ["md"],
      },
    ];
  };

  const data = useMemo(() => summaryData?.result, [summaryData]);
  const { baseRow, covaRows, standardRows } = useMemo(() => {
    if (!data || props.type !== SUBSCRIPTION_TYPE.STANDARD) {
      return {
        baseRow: undefined,
        covaRows: [] as SummaryRow[],
        standardRows: [] as SummaryRow[],
      };
    }

    const { covaList = [], ...base } = data;

    const baseRow: SummaryRow = {
      ...base,
      key: "base",
      registeredChannel: base.registeredChannel ?? 0,
      expiration:
        base.registeredChannel && base.registeredChannel > 0
          ? base.expiration
          : "",
    };

    const covaRows: SummaryRow[] = covaList.map(
      (cova: SubscriptionDetailSummaryBase, idx: number) => ({
        ...cova,
        key: `cova-${idx}`,
        registeredChannel: cova.registeredChannel ?? 0,
      })
    );

    return {
      baseRow,
      covaRows,
      standardRows: [baseRow, ...covaRows],
    };
  }, [data, props.type]);
  const rows = useMemo(() => {
    if (!data) return [];
    return buildTableRows(data);
  }, [data, buildTableRows]);

  return (
    <Wrapper>
      <TableSection>
        {isStandard && baseRow !== undefined && covaRows.length > 0 ? (
          <SplitTables isDesktop={isDesktop}>
            <SplitCard>
              <CommonTable
                columns={columns()}
                dataSource={[baseRow]}
                rowKey="key"
              />
            </SplitCard>
            <SplitCard>
              <CommonTable
                columns={columns(true)}
                dataSource={covaRows}
                rowKey="key"
                scroll={{ y: isDesktop ? 360 : 260 }}
              />
            </SplitCard>
          </SplitTables>
        ) : isStandard ? (
          <CommonTable
            columns={columns(baseRow === undefined)}
            dataSource={standardRows}
            rowKey="key"
            scroll={standardRows.length > 8 ? { y: 360 } : undefined}
          />
        ) : (
          <CommonTable columns={columns()} dataSource={rows} rowKey="key" />
        )}
      </TableSection>
      {isModalStorageChange && (
        <SubscriptionStorageChangeModal
          onModal={onModal}
          activatedStorage={summaryData?.result?.activated ?? 0}
        />
      )}

      {isModalCoterm && (
        <SubscriptonCotermWork
          onModal={onModalCotermWork}
          selectedRowAccount={props.selectedRowAccount}
          data={summaryData?.result ?? {}}
        />
      )}
    </Wrapper>
  );
}

type CovaFreeKeyRenewProps = {
  accountId: string;
  dwKey?: string;
};

function CovaFreeKeyRenew({
  accountId,
  dwKey,
}: CovaFreeKeyRenewProps): JSX.Element {
  const { handleErrorNotify, lang, notifySuccess } = useApiError();
  const queryClient = useQueryClient();
  const [renewDate, setRenewDate] = useState<string>("");

  const invalidateSummary = () => {
    queryClient.invalidateQueries("getSubscriptionDetailSummary");
    queryClient.invalidateQueries("getSubscriptionKeyList");
  };

  const mutationRenew = useStatusMutation(postCovaFreeKeyRenew, {
    onSuccess: () => {
      notifySuccess(lang.label.subscription.cova.renew.success);
      invalidateSummary();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.cova.renew.fail);
    },
  });

  const mutationReset = useStatusMutation(postCovaFreeKeyReset, {
    onSuccess: () => {
      notifySuccess(lang.label.subscription.cova.reset.success);
      setRenewDate("");
      invalidateSummary();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.cova.reset.fail);
    },
  });

  const handleRenew = () => {
    if (!dwKey || !mutationRenew.idle) return;
    mutationRenew.mutate({ accountId, dwKey });
  };

  const handleSetDate = () => {
    if (!dwKey || !renewDate || !mutationReset.idle) return;
    mutationReset.mutate({ accountId, dwKey, expirationDate: renewDate });
  };

  return (
    <RBACWrapper requiredPermissions="manageSubscription">
      <RenewControls>
        <TinyButton
          label="Renew"
          icon={<FaArrowsRotate />}
          onClickBtn={handleRenew}
          disabled={!dwKey || !mutationRenew.idle}
        />
        <DateSetGroup>
          <CompactDateInput>
            <InputDate
              value={renewDate}
              onChange={(value) => setRenewDate(value as string)}
              placeholder="Set Expire date"
              portal
              calendarPosition="bottom-right"
            />
          </CompactDateInput>
          <TinyButton
            label="Set"
            onClickBtn={handleSetDate}
            disabled={!dwKey || !renewDate || !mutationReset.idle}
          />
        </DateSetGroup>
      </RenewControls>
    </RBACWrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
`;

const BarWrapper = styled.div`
  max-width: 400px;

  ${isMobile} {
    max-width: 150px;
    width: 150px;
    > div {
      min-width: 150px;
    }
  }
`;

const ActivatedValue = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 8px;
`;

const ExpireRow = styled.div`
  ${FLEX.FlexStartCenter};
  column-gap: 10px;
  row-gap: 3px;
  flex-wrap: wrap;
`;

const TableSection = styled.div`
  margin-bottom: 16px;

  .expire-title {
    ${FLEX.FlexStartCenter};
    gap: 4px;
  }
  .cova-free {
    font-weight: 500;
    color: ${({ theme }) => theme.COLORS.SELECTED};
    padding: 2px 4px;
    border-radius: 2px;
    font-size: ${calcRem(12)};
  }
`;

const SplitTables = styled.div<{ isDesktop: boolean }>`
  display: grid;
  grid-template-columns: ${({ isDesktop }) => (isDesktop ? "1fr 1fr" : "1fr")};
  align-items: start;
  gap: 16px;
`;

const SplitCard = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  align-self: start;

  .ant-table-wrapper {
    height: auto;
  }
  .ant-table-wrapper > div,
  .ant-spin-container,
  .ant-table,
  .ant-table-container,
  .ant-table-content {
    height: auto !important;
  }
`;

const RenewControls = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 8px;
  flex-wrap: wrap;
`;

const DateSetGroup = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 6px;
`;

const CompactDateInput = styled.div`
  .date-picker-wrapper {
    width: 90px;
    max-width: 90px;
    height: 24px;
    padding: 6px;
    border-radius: 3px;
  }

  .date-picker-wrapper > svg {
    display: none;
  }
  .rmdp-input {
    width: 100%;
    height: 24px;
    padding: 0;
    font-size: ${calcRem(12)};
  }
`;
